import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EnvironmentService } from './environment.service';
import { PersistenceService } from './persistence.service';
import {
    TravelApiEnvelope,
    TravelAuthPayload,
    TravelChallengeResultsProgress,
    TravelChallengeResultsUpdate,
    TravelDetail,
    TravelListItem,
    TravelPackingChecklistUpdate,
    TravelProgressResponse,
    TravelUser,
} from '../models/travel.model';

const TOKEN_KEY = 'TRAVEL_AuthToken';
const USER_KEY = 'TRAVEL_User';
const TRAVEL_ID_KEY = 'TRAVEL_CurrentId';

@Injectable({
    providedIn: 'root'
})
export class TravelDataService {
    private _token: string = null;
    private _user: TravelUser = null;
    private _currentTravelId: number = null;
    private _currentTravel: TravelDetail = null;
    private _travelList: TravelListItem[] = [];
    private readonly http: HttpClient;

    readonly authChange = new Subject<void>();
    readonly travelChange = new Subject<TravelDetail | null>();

    constructor(
        httpBackend: HttpBackend,
        private environmentService: EnvironmentService,
        private persistenceService: PersistenceService,
    ) {
        // Bypass ApiInterceptor to avoid AuthService ↔ HttpClient circular DI,
        // and because travel auth uses its own Bearer token.
        this.http = new HttpClient(httpBackend);
    }

    /** Travel API is enabled when TravelAPIUrl is configured. */
    isEnabled() {
        const url = this.environmentService.environment?.TravelAPIUrl;
        return typeof url === 'string' && url.length > 0;
    }

    get baseUrl(): string {
        return this.environmentService.environment.TravelAPIUrl;
    }

    get token(): string {
        return this._token || this.persistenceService.getItem(TOKEN_KEY);
    }

    get user(): TravelUser {
        if (this._user) return this._user;
        const raw = this.persistenceService.getItem(USER_KEY);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as TravelUser;
        } catch {
            return null;
        }
    }

    get currentTravelId(): number {
        return this._currentTravelId;
    }

    get currentTravel(): TravelDetail {
        return this._currentTravel;
    }

    get travelList(): TravelListItem[] {
        return this._travelList;
    }

    isLoggedIn() {
        return !!this.token && !!this.user;
    }

    /** Restore session from persistence (call from app init). */
    restoreSession(): Observable<TravelUser | null> {
        if (!this.isEnabled()) {
            return new Observable((observer) => {
                observer.next(null);
                observer.complete();
            });
        }

        const token = this.token;
        if (!token) {
            return new Observable((observer) => {
                observer.next(null);
                observer.complete();
            });
        }

        this._token = token;
        return this.me().pipe(
            tap((user) => {
                this._setSession(token, user);
            }),
            catchError(() => {
                this.clearSession();
                return new Observable<TravelUser | null>((observer) => {
                    observer.next(null);
                    observer.complete();
                });
            })
        );
    }

    login(email: string, password: string): Observable<TravelAuthPayload> {
        return this.http.post<TravelApiEnvelope<TravelAuthPayload>>(
            this.baseUrl + '/login',
            { email, password },
            { headers: this._jsonHeaders(false) }
        ).pipe(
            map((res) => this._unwrap(res)),
            tap((data) => this._setSession(data.token, data.user))
        );
    }

    requestOtpEmail(email: string): Observable<any> {
        return this.http.post(
            this.baseUrl + '/otp/email/' + encodeURIComponent(email),
            {},
            { headers: this._jsonHeaders(false) }
        );
    }

    requestOtpPhone(phone: string): Observable<any> {
        return this.http.post(
            this.baseUrl + '/otp/phone/' + encodeURIComponent(phone),
            {},
            { headers: this._jsonHeaders(false) }
        );
    }

    requestOtp(contact: string, type: 'email' | 'phone'): Observable<any> {
        return type === 'email'
            ? this.requestOtpEmail(contact)
            : this.requestOtpPhone(contact);
    }

    loginOtp(body: { email?: string; phone?: string; code: string }): Observable<TravelAuthPayload> {
        return this.http.post<TravelApiEnvelope<TravelAuthPayload>>(
            this.baseUrl + '/otp/login',
            body,
            { headers: this._jsonHeaders(false) }
        ).pipe(
            map((res) => this._unwrap(res)),
            tap((data) => this._setSession(data.token, data.user))
        );
    }

    me(): Observable<TravelUser> {
        return this.http.get<TravelApiEnvelope<TravelUser> | TravelUser>(
            this.baseUrl + '/me',
            { headers: this._jsonHeaders(true) }
        ).pipe(
            map((res) => {
                if (res && typeof res === 'object' && 'data' in (res as any)) {
                    return this._unwrap(res as TravelApiEnvelope<TravelUser>);
                }
                return res as TravelUser;
            }),
            tap((user) => {
                this._user = user;
                this.persistenceService.setItem(USER_KEY, JSON.stringify(user), 'technical');
            })
        );
    }

    logout(): Observable<void> {
        if (!this.token) {
            this.clearSession();
            return new Observable((observer) => {
                observer.next();
                observer.complete();
            });
        }

        return this.http.post(
            this.baseUrl + '/logout',
            {},
            { headers: this._jsonHeaders(true) }
        ).pipe(
            map(() => undefined as void),
            catchError(() => {
                // Clear local session even if server logout fails
                return new Observable<void>((observer) => {
                    observer.next();
                    observer.complete();
                });
            }),
            tap(() => this.clearSession())
        );
    }

    listTravels(): Observable<TravelListItem[]> {
        return this.http.get<TravelApiEnvelope<TravelListItem[]>>(
            this.baseUrl + '/travels',
            { headers: this._jsonHeaders(true) }
        ).pipe(
            map((res) => this._unwrap(res) || []),
            tap((list) => {
                this._travelList = list || [];
            })
        );
    }

    getTravel(id: number | string): Observable<TravelDetail> {
        return this.http.get<TravelApiEnvelope<TravelDetail>>(
            this.baseUrl + '/travels/' + id,
            { headers: this._jsonHeaders(true) }
        ).pipe(
            map((res) => this._unwrap(res)),
            tap((travel) => {
                this._setCurrentTravel(travel);
            })
        );
    }

    /** Load list and open preferred/persisted/first travel. */
    loadCurrentTravel(preferredId?: number): Observable<TravelDetail | null> {
        return this.listTravels().pipe(
            switchMap((list) => {
                if (!list?.length) {
                    this._setCurrentTravel(null);
                    return of(null);
                }

                const persistedId = this._readPersistedTravelId();
                const wantedId = preferredId ?? this._currentTravelId ?? persistedId;
                const item = (wantedId != null ? list.find((t) => t.id === wantedId) : null) || list[0];
                return this.getTravel(item.id);
            })
        );
    }

    /** Switch to another travel from the loaded list (or any known id). */
    selectTravel(id: number | string): Observable<TravelDetail | null> {
        const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
        if (numericId == null || Number.isNaN(numericId)) {
            return of(this._currentTravel);
        }
        if (this._currentTravelId === numericId && this._currentTravel) {
            return of(this._currentTravel);
        }
        return this.getTravel(numericId);
    }

    updatePackingChecklist(travelId: number | string, body: TravelPackingChecklistUpdate): Observable<TravelProgressResponse> {
        return this.http.put<TravelApiEnvelope<TravelProgressResponse>>(
            this.baseUrl + '/travels/' + travelId + '/packing-checklist',
            body,
            { headers: this._jsonHeaders(true) }
        ).pipe(
            map((res) => this._unwrap(res)),
            tap((data) => {
                if (this._currentTravel && this._currentTravel.id === data.travel_id) {
                    this._currentTravel.progress = data.progress;
                    this._applyPackingCheckedToTravel(data.progress?.packing_checklist?.checked);
                    this.travelChange.next(this._currentTravel);
                }
            })
        );
    }

    updateChallengeResults(travelId: number | string, body: TravelChallengeResultsUpdate): Observable<TravelProgressResponse> {
        return this.http.put<TravelApiEnvelope<TravelProgressResponse>>(
            this.baseUrl + '/travels/' + travelId + '/challenge-results',
            body,
            { headers: this._jsonHeaders(true) }
        ).pipe(
            map((res) => this._unwrap(res)),
            tap((data) => {
                if (this._currentTravel && this._currentTravel.id === data.travel_id) {
                    this._currentTravel.progress = data.progress;
                    this._applyChallengeResultsToTravel(data.progress?.challenge_results);
                    this.travelChange.next(this._currentTravel);
                }
            })
        );
    }

    /** Flatten travel_challenges into rows for totem-widget-challenge. */
    challengesToChallengeList(travel?: TravelDetail): Array<{
        mission: string;
        objective: string;
        type?: 'photo' | 'question' | 'multiple_choice' | 'checkin';
        checked?: boolean;
        answer?: string;
        id?: string;
        destination?: string;
        resultKey?: string;
        points?: number;
        options?: string[];
        correctAnswer?: string;
        explanation?: string;
    }> {
        const current = travel || this._currentTravel;
        const byDestination = current?.travel_challenges?.by_destination;
        if (!byDestination) return [];

        const progressResults = current?.progress?.challenge_results?.results || {};
        const rows = [];

        for (const destination of Object.keys(byDestination)) {
            for (const challenge of byDestination[destination]?.challenges || []) {
                const resultKey = challenge.result_key || (destination + ':' + challenge.id);
                const result = challenge.user_result || progressResults[resultKey] || null;
                const status = result?.status;
                const checked = status === 'completed' || status === 'passed';
                const type = this._mapChallengeType(challenge.type);

                rows.push({
                    id: challenge.id,
                    destination,
                    resultKey,
                    points: challenge.points,
                    mission: challenge.question
                        || challenge.title
                        || challenge.description
                        || challenge.id,
                    objective: challenge.category
                        || challenge.location_name
                        || challenge.location_hint
                        || challenge.what_to_do
                        || challenge.description
                        || '',
                    type,
                    options: challenge.options,
                    correctAnswer: challenge.correct_answer,
                    explanation: challenge.explanation,
                    checked,
                    answer: result?.answer != null ? String(result.answer) : undefined,
                });
            }
        }

        return rows;
    }

    /** Flatten packing_list into checklist rows for totem-widget-checklist. */
    packingToCheckList(travel?: TravelDetail): Array<{ label: string; description?: string; checked?: boolean; key?: string; category?: string }> {
        const tips = (travel || this._currentTravel)?.packing_tips;
        const list = tips?.packing_list;
        if (!list) return [];

        const checkedMap = (travel || this._currentTravel)?.progress?.packing_checklist?.checked || {};
        const rows = [];

        for (const category of Object.keys(list)) {
            for (const entry of list[category] || []) {
                const key = entry.key || (category + ':' + entry.item);
                rows.push({
                    label: entry.item,
                    description: entry.note
                        || entry.reason
                        || (entry.quantity != null ? ('×' + entry.quantity) : undefined),
                    checked: entry.checked != null ? !!entry.checked : !!checkedMap[key],
                    key,
                    category
                });
            }
        }

        return rows;
    }

    /**
     * Map content.transport flights/transfers and content.accommodation
     * into totem-widget-details rows, sorted by date.
     * When onDate (YYYY-MM-DD) is set: flights/transfers match that day;
     * accommodation is included when onDate is within [check_in, check_out].
     */
    transportToDetailList(travel?: TravelDetail, options?: { onDate?: string }): Array<{
        icon: string;
        title: string;
        subtitle?: string;
        description?: string;
        date?: string;
    }> {
        const content = (travel || this._currentTravel)?.content;
        const onDate = this._toDateKey(options?.onDate);
        const rows: Array<{
            icon: string;
            title: string;
            subtitle?: string;
            description?: string;
            date?: string;
            sortKey: number;
        }> = [];

        for (const flight of content?.transport?.flights || []) {
            if (onDate && this._toDateKey(flight.date) !== onDate) continue;
            const title = [flight.airline, flight.flight_number].filter(Boolean).join(' ')
                || [flight.from, flight.to].filter(Boolean).join(' → ')
                || 'Flight';
            const left = [flight.from, flight.departure_time].filter(Boolean).join(' ');
            const right = [flight.to, flight.arrival_time].filter(Boolean).join(' ');
            rows.push({
                icon: 'fa-plane',
                title,
                subtitle: [left, right].filter(Boolean).join(' - ') || undefined,
                date: flight.date || undefined,
                sortKey: this._travelTransportSortKey(flight.date, flight.departure_time),
            });
        }

        for (const transfer of content?.transport?.transfers || []) {
            if (onDate && this._toDateKey(transfer.date) !== onDate) continue;
            rows.push({
                icon: 'fa-car',
                title: transfer.type ? String(transfer.type).replace(/_/g, ' ') : 'Transfer',
                subtitle: [transfer.from, transfer.to].filter(Boolean).join(' → ') || undefined,
                date: transfer.date || undefined,
                sortKey: this._travelTransportSortKey(transfer.date),
            });
        }

        for (const stay of content?.accommodation || []) {
            if (!stay) continue;
            if (onDate && !this._isDateInInclusiveRange(onDate, stay.check_in, stay.check_out)) continue;
            const title = (stay.name || stay.city || 'Hotel').trim();
            const subtitle = [stay.city, stay.room_type].filter(Boolean).join(' · ') || undefined;
            const nights = stay.nights != null ? (stay.nights + (stay.nights === 1 ? ' night' : ' nights')) : null;
            const dates = [stay.check_in, stay.check_out].filter(Boolean).join(' → ');
            const description = [
                dates || null,
                nights,
                stay.meal_plan,
                stay.address,
                stay.phone,
            ].filter(Boolean).join(' · ') || undefined;

            rows.push({
                icon: 'fa-hotel',
                title,
                subtitle,
                description,
                date: stay.check_in || undefined,
                sortKey: this._travelTransportSortKey(stay.check_in || undefined),
            });
        }

        rows.sort((a, b) => a.sortKey - b.sortKey);
        return rows.map(({ sortKey, ...row }) => row);
    }

    private _toDateKey(value?: string | Date | null): string | null {
        if (value == null || value === '') return null;
        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) return null;
            const y = value.getFullYear();
            const m = String(value.getMonth() + 1).padStart(2, '0');
            const d = String(value.getDate()).padStart(2, '0');
            return y + '-' + m + '-' + d;
        }
        const raw = String(value).trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) return null;
        return this._toDateKey(parsed);
    }

    /** Inclusive range: check_in <= onDate <= check_out (missing bound = open). */
    private _isDateInInclusiveRange(
        onDate: string,
        checkIn?: string | null,
        checkOut?: string | null,
    ): boolean {
        const from = this._toDateKey(checkIn);
        const to = this._toDateKey(checkOut);
        if (from && onDate < from) return false;
        if (to && onDate > to) return false;
        return !!(from || to);
    }

    private _travelTransportSortKey(date?: string, time?: string): number {
        if (!date) return Number.MAX_SAFE_INTEGER;
        const timePart = (time || '00:00').replace(/[^0-9:]/g, '').slice(0, 5);
        const normalizedTime = /^\d{1,2}:\d{2}$/.test(timePart) ? timePart.padStart(5, '0') : '00:00';
        const parsed = Date.parse(date + 'T' + normalizedTime + ':00');
        return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
    }

    /** Map content.program into totem-widget-activities days. */
    programToActivityList(travel?: TravelDetail): Array<{
        date: string;
        day?: number;
        location?: string;
        activities: Array<{ title: string; description?: string }>;
    }> {
        const current = travel || this._currentTravel;
        const program = current?.content?.program;
        if (!program?.length) return [];

        const departure = current?.travel_period?.departure_date
            || current?.content?.travel_period?.departure_date;
        const base = departure ? new Date(departure + 'T00:00:00') : null;

        return program.map((day) => {
            let dateIso: string;
            if (base && day.day != null) {
                const d = new Date(base);
                d.setDate(d.getDate() + Math.max(0, day.day - 1));
                dateIso = d.toISOString();
            }
            else {
                dateIso = new Date().toISOString();
            }

            return {
                date: dateIso,
                day: day.day,
                location: day.location || undefined,
                activities: [{
                    title: day.location || ('Day ' + (day.day ?? '')),
                    description: day.description,
                }],
            };
        });
    }

    /**
     * Map content.insurance into custom products for totem-widget-products.
     * Priority:
     * 1) non-empty policies[] → one card per policy (root + policies)
     * 2) coverages[] → one card per coverage
     * 3) medical/cancellation/baggage booleans
     */
    insuranceToProductsData(travel?: TravelDetail, language?: string): Array<any> {
        const insurance = (travel || this._currentTravel)?.content?.insurance;
        if (!insurance) return [];

        const it = (language || 'it').toLowerCase().startsWith('it');
        const labels = {
            medical: it ? 'Medica' : 'Medical',
            cancellation: it ? 'Cancellazione' : 'Cancellation',
            baggage: it ? 'Bagaglio' : 'Baggage',
            included: it ? 'Incluso' : 'Included',
            optional: it ? 'Facoltativa' : 'Optional',
            title: it ? 'Assicurazione di viaggio' : 'Travel insurance',
        };

        if (Array.isArray(insurance.policies) && insurance.policies.length) {
            const policyList: Array<Omit<typeof insurance, 'policies'>> = [];

            if (insurance.policy_name || insurance.coverages?.length) {
                const { policies: _ignored, ...rootPolicy } = insurance;
                policyList.push(rootPolicy);
            }
            for (const policy of insurance.policies) {
                if (policy) policyList.push(policy);
            }

            return policyList.map((policy, index) => this._insurancePolicyToProduct(policy, index, labels));
        }

        if (Array.isArray(insurance.coverages) && insurance.coverages.length) {
            return insurance.coverages
                .filter((coverage) => !!coverage)
                .map((coverage, index) => this._insuranceCoverageToProduct(coverage, index, insurance, labels));
        }

        const details = insurance.details || '';
        const includedLink = this._insuranceIncludedLink(labels);

        const covers: Array<{ id: string; title: string; included: boolean }> = [
            { id: 'custom_insurance_medical', title: labels.medical, included: !!insurance.medical },
            { id: 'custom_insurance_cancellation', title: labels.cancellation, included: !!insurance.cancellation },
            { id: 'custom_insurance_baggage', title: labels.baggage, included: !!insurance.baggage },
        ].filter((c) => c.included);

        if (!covers.length && details) {
            return [{
                id: 'custom_insurance_details',
                type: 'custom',
                resource: 'custom',
                title: insurance.policy_name || labels.title,
                description: details,
                link: includedLink,
            }];
        }

        return covers.map((cover) => ({
            id: cover.id,
            type: 'custom',
            resource: 'custom',
            title: cover.title,
            description: details || undefined,
            link: { ...includedLink },
        }));
    }

    private _insuranceIncludedLink(labels: { included: string; optional: string }, included = true) {
        return included
            ? {
                color: 'success',
                icon: 'fa-regular fa-check',
                text: labels.included,
            }
            : {
                color: 'secondary',
                icon: 'fa-regular fa-circle-info',
                text: labels.optional,
            };
    }

    private _insurancePolicyToProduct(
        policy: {
            details?: string | null;
            provider?: string;
            policy_name?: string;
            policy_number?: string;
            included?: boolean;
            coverages?: Array<{
                area?: string | null;
                type?: string;
                description?: string;
                limit_amount?: number;
                currency?: string;
            }>;
        },
        index: number,
        labels: { included: string; optional: string; title: string },
    ) {
        const title = (policy.policy_name || policy.provider || labels.title).trim();
        return {
            id: policy.policy_number
                ? ('custom_insurance_' + policy.policy_number)
                : ('custom_insurance_policy_' + index),
            type: 'custom',
            resource: 'custom',
            title,
            description: this._formatInsurancePolicyDescription(policy) || undefined,
            link: this._insuranceIncludedLink(labels, policy.included !== false),
        };
    }

    private _insuranceCoverageToProduct(
        coverage: {
            area?: string | null;
            type?: string;
            description?: string;
            limit_amount?: number;
            currency?: string;
            included?: boolean;
        },
        index: number,
        insurance: {
            details?: string | null;
            policy_name?: string;
            provider?: string;
        },
        labels: { medical: string; cancellation: string; baggage: string; included: string; optional: string },
    ) {
        const typeLabel = coverage.type && labels[coverage.type] ? labels[coverage.type] : coverage.type;
        const title = (coverage.description || typeLabel || '').trim();
        const limit = coverage.limit_amount != null
            ? ((coverage.currency || 'EUR') + ' ' + coverage.limit_amount.toLocaleString('it-IT'))
            : null;
        const description = [
            insurance.details || insurance.policy_name,
            coverage.area,
            limit,
            insurance.provider,
        ].filter(Boolean).join('\n');

        return {
            id: 'custom_insurance_coverage_' + (coverage.type || index),
            type: 'custom',
            resource: 'custom',
            title,
            description: description || undefined,
            link: this._insuranceIncludedLink(labels, coverage.included !== false),
        };
    }

    private _formatInsurancePolicyDescription(policy: {
        details?: string | null;
        provider?: string;
        policy_number?: string;
        coverages?: Array<{
            area?: string | null;
            type?: string;
            description?: string;
            limit_amount?: number;
            currency?: string;
        }>;
    }): string {
        const parts: string[] = [];

        if (policy.details) parts.push(String(policy.details).trim());

        const meta = [policy.provider, policy.policy_number].filter(Boolean).join(' · ');
        if (meta) parts.push(meta);

        for (const coverage of policy.coverages || []) {
            if (!coverage) continue;
            const limit = coverage.limit_amount != null
                ? ((coverage.currency || 'EUR') + ' ' + coverage.limit_amount.toLocaleString('it-IT'))
                : null;
            const line = [coverage.description || coverage.type, coverage.area, limit]
                .filter(Boolean)
                .join(' — ');
            if (line) parts.push(line);
        }

        return parts.join('\n');
    }

    clearSession() {
        this._token = null;
        this._user = null;
        this._travelList = [];
        this.persistenceService.removeItem(TOKEN_KEY);
        this.persistenceService.removeItem(USER_KEY);
        this.persistenceService.removeItem(TRAVEL_ID_KEY);
        this._setCurrentTravel(null, false);
        this.authChange.next();
    }

    private _setSession(token: string, user: TravelUser) {
        this._token = token;
        this._user = user;
        this.persistenceService.setItem(TOKEN_KEY, token, 'technical');
        this.persistenceService.setItem(USER_KEY, JSON.stringify(user), 'technical');
        this.authChange.next();
    }

    private _setCurrentTravel(travel: TravelDetail | null, persist = true) {
        this._currentTravelId = travel?.id ?? null;
        this._currentTravel = travel;
        if (persist) {
            if (this._currentTravelId != null) {
                this.persistenceService.setItem(TRAVEL_ID_KEY, String(this._currentTravelId), 'technical');
            }
            else {
                this.persistenceService.removeItem(TRAVEL_ID_KEY);
            }
        }
        this.travelChange.next(travel);
    }

    private _readPersistedTravelId(): number | null {
        const raw = this.persistenceService.getItem(TRAVEL_ID_KEY);
        if (raw == null || raw === '') return null;
        const id = parseInt(raw, 10);
        return Number.isNaN(id) ? null : id;
    }

    private _applyPackingCheckedToTravel(checked: { [key: string]: boolean }) {
        if (!this._currentTravel?.packing_tips?.packing_list || !checked) return;
        for (const category of Object.keys(this._currentTravel.packing_tips.packing_list)) {
            for (const entry of this._currentTravel.packing_tips.packing_list[category] || []) {
                const key = entry.key || (category + ':' + entry.item);
                if (key in checked) entry.checked = !!checked[key];
            }
        }
    }

    private _applyChallengeResultsToTravel(progress?: TravelChallengeResultsProgress) {
        const byDestination = this._currentTravel?.travel_challenges?.by_destination;
        if (!byDestination || !progress?.results) return;

        for (const destination of Object.keys(byDestination)) {
            for (const challenge of byDestination[destination]?.challenges || []) {
                const resultKey = challenge.result_key || (destination + ':' + challenge.id);
                if (progress.results[resultKey]) {
                    challenge.user_result = progress.results[resultKey];
                }
            }
        }
    }

    private _mapChallengeType(type?: string): 'photo' | 'question' | 'multiple_choice' | 'checkin' {
        const normalized = (type || '').toLowerCase();
        if (normalized.includes('photo') || normalized.includes('image') || normalized.includes('camera')) {
            return 'photo';
        }
        if (normalized.includes('checkin') || normalized.includes('check-in') || normalized.includes('check_in')) {
            return 'checkin';
        }
        if (normalized.includes('multiple') || normalized.includes('choice') || normalized.includes('quiz')) {
            return 'multiple_choice';
        }
        return 'question';
    }

    private _jsonHeaders(withAuth: boolean): HttpHeaders {
        let headers = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        });
        if (withAuth && this.token) {
            headers = headers.set('Authorization', 'Bearer ' + this.token);
        }
        return headers;
    }

    private _unwrap<T>(res: TravelApiEnvelope<T>): T {
        if (res == null) {
            throw new Error('Empty travel API response');
        }
        if (res.success === false) {
            throw new Error(res.message || 'Travel API error');
        }
        return res.data;
    }
}
